import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, Trash2, ChevronLeft, Eye, CheckCheck } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { formatDateTime } from '../utils/timeUtils';

interface Message {
  id: string;
  title: string;
  content: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export default function MessagePage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'messages_get_messages', userId: user.id })
    });
    const result = await response.json();

    if (result.success) {
      const msgs = result.data || [];
      setMessages(msgs);
      setUnreadCount(msgs.filter((m: Message) => !m.is_read).length);
    }
    setLoading(false);
  };

  const markAsRead = async (messageId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'messages_mark_as_read', messageId })
    });

    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true } : m));
    setUnreadCount(prev => Math.max(0, prev - 1));

    // 通知 Header 组件更新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const markAllRead = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'messages_mark_all_read', userId: user.id })
    });

    setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
    setUnreadCount(0);

    // 通知 Header 组件更新未读数
    window.dispatchEvent(new Event('message-status-change'));
  };

  const deleteMessage = async (messageId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'messages_delete', messageId })
    });

    setMessages(prev => prev.filter(m => m.id !== messageId));
  };

  const openMessage = (msg: Message) => {
    setSelectedMessage(msg);
    if (!msg.is_read) markAsRead(msg.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => window.history.back()} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Mail className="w-6 h-6 text-amber-600" />
                我的站内信
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    未读 {unreadCount}
                  </span>
                )}
              </h1>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 px-4 py-2 text-amber-700 hover:bg-amber-50 rounded-lg transition-colors text-sm"
              >
                <CheckCheck className="w-4 h-4" />
                全部标已读
              </button>
            )}
          </div>

          {messages.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${!msg.is_read ? 'bg-amber-50/30' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      msg.is_read ? 'bg-gray-100' : 'bg-amber-100'
                    }`}>
                      <Mail className={`w-5 h-5 ${msg.is_read ? 'text-gray-500' : 'text-amber-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-medium truncate ${msg.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                          {msg.title}
                        </h3>
                        {!msg.is_read && <span className="w-2 h-2 bg-amber-500 rounded-full"></span>}
                      </div>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-1">{msg.content.replace(/<[^>]*>/g, '').trim()}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDateTime(msg.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openMessage(msg)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!msg.is_read && (
                        <button
                          onClick={() => markAsRead(msg.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <p className="text-gray-500">暂无消息</p>
            </div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">{selectedMessage.title}</h3>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {formatDateTime(selectedMessage.created_at)}
              </p>
              <div
                className="bg-gray-50 rounded-lg p-4 text-gray-700 max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: selectedMessage.content }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
